import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function Settings({ logout }) {
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Get current user from localStorage
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : null;
  console.log('Settings user:', user, 'role:', user?.role); // DEBUG
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  
  // Rules state - accessible to all users
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState('');
  
  // Rule modals
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showEditRuleModal, setShowEditRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    keywords: '',
    platforms: '',
    categories: '',
    description: '',
    isActive: true
  });
  const [ruleFormLoading, setRuleFormLoading] = useState(false);
  const [ruleFormMessage, setRuleFormMessage] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'USER' });
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN';

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setUsersLoading(true);
    setUsersError('');
    try {
      const { data } = await axios.get('/api/users');
      setUsers(data);
    } catch (err) {
      setUsersError('Failed to load users');
      console.error(err);
    }
    setUsersLoading(false);
  }, [isAdmin]);

  // Fetch rules (all users)
  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    setRulesError('');
    try {
      const { data } = await axios.get('/api/rules');
      setRules(data);
    } catch (err) {
      setRulesError('Failed to load rules');
      console.error(err);
    }
    setRulesLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchRules();
  }, [fetchUsers, fetchRules]);

  // Password reset (existing)
  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      await axios.post('/api/auth/reset-password', { password });
      setMessage('Password reset successful');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setMessage('Reset failed');
    }
    setLoading(false);
  };

  // Form handlers
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormMessage('');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password || formData.password.length < 6) {
      setFormMessage('Valid email and password (min 6 chars) required');
      return;
    }
    setFormLoading(true);
    try {
      await axios.post('/api/users', formData);
      setFormMessage('User created successfully');
      setFormData({ email: '', password: '', role: 'USER' });
      setShowAddModal(false);
      fetchUsers();
    } catch (err) {
      setFormMessage(err.response?.data?.error || 'Failed to create user');
    }
    setFormLoading(false);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`/api/users/${editingUser.id}`, formData);
      setFormMessage('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setFormMessage(err.response?.data?.error || 'Failed to update user');
    }
    setFormLoading(false);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ email: user.email, role: user.role, password: '' });
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/users/${id}`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  // Rule handlers
  const handleRuleInputChange = (e) => {
    setRuleFormData({ ...ruleFormData, [e.target.name]: e.target.value });
    setRuleFormMessage('');
  };

  const parseArrayInput = (input) => input.split(',').map(s => s.trim()).filter(Boolean);

  const serializeArrayInput = (arr) => arr.join(', ');

  const handleAddRule = async (e) => {
    e.preventDefault();
    if (!ruleFormData.name || ruleFormData.name.length < 3) {
      setRuleFormMessage('Rule name required (min 3 chars)');
      return;
    }
    setRuleFormLoading(true);
    try {
      await axios.post('/api/rules', {
        ...ruleFormData,
        keywords: parseArrayInput(ruleFormData.keywords),
        platforms: parseArrayInput(ruleFormData.platforms),
        categories: parseArrayInput(ruleFormData.categories)
      });
      setRuleFormMessage('Rule created successfully');
      setRuleFormData({ name: '', keywords: '', platforms: '', categories: '', description: '', isActive: true });
      setShowAddRuleModal(false);
      fetchRules();
    } catch (err) {
      setRuleFormMessage(err.response?.data?.error || 'Failed to create rule');
    }
    setRuleFormLoading(false);
  };

  const handleEditRule = async (e) => {
    e.preventDefault();
    setRuleFormLoading(true);
    try {
      await axios.put(`/api/rules/${editingRule.id}`, {
        ...ruleFormData,
        keywords: parseArrayInput(ruleFormData.keywords),
        platforms: parseArrayInput(ruleFormData.platforms),
        categories: parseArrayInput(ruleFormData.categories)
      });
      setRuleFormMessage('Rule updated successfully');
      setShowEditRuleModal(false);
      setEditingRule(null);
      fetchRules();
    } catch (err) {
      setRuleFormMessage(err.response?.data?.error || 'Failed to update rule');
    }
    setRuleFormLoading(false);
  };

  const handleEditRuleClick = (rule) => {
    setEditingRule(rule);
    setRuleFormData({
      name: rule.name,
      keywords: serializeArrayInput(rule.keywords),
      platforms: serializeArrayInput(rule.platforms),
      categories: serializeArrayInput(rule.categories),
      description: rule.description || '',
      isActive: rule.isActive
    });
    setShowEditRuleModal(true);
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Delete this rule? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/rules/${id}`);
      fetchRules();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete rule');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Settings</h2>
        
        {/* Debug Info */}
        <div className="mb-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Info:</h3>
          <p className="text-sm text-gray-600">User: {user ? `${user.email} (${user.role})` : 'Not logged in'}</p>
          <p className="text-sm text-gray-600">isAdmin: {isAdmin ? 'Yes' : 'No'}</p>
        </div>
        
        {/* Password Reset Section */}
        <div className="max-w-md bg-white shadow rounded-lg p-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Password</h3>
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Reset Password'}
            </button>
          </form>
          {message && (
            <div className={`mt-4 p-3 rounded-md text-sm ${
              message.includes('successful') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message}
            </div>
          )}
          <button
            onClick={logout}
            className="w-full mt-4 text-red-600 hover:text-red-700 font-medium"
          >
            Logout
          </button>
        </div>
      </div>

      {/* User Management - Admin Only */}
      {isAdmin && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">User Management (Admin)</h3>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium"
            >
              + Add User
            </button>
          </div>

          {/* Users Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {usersLoading ? (
              <div className="p-8 text-center text-gray-500">Loading users...</div>
            ) : usersError ? (
              <div className="p-8 text-center text-red-600">{usersError}</div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No users found</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.role === 'ADMIN' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Add User Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-medium mb-4">Add New User</h3>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      name="email"
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                    <input
                      name="password"
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.password}
                      onChange={handleInputChange}
                      minLength={6}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      name="role"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.role}
                      onChange={handleInputChange}
                    >
                      <option value="USER">User</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </div>
                  {formMessage && (
                    <div className={`p-3 rounded-md text-sm ${
                      formMessage.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      {formMessage}
                    </div>
                  )}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                    >
                      {formLoading ? 'Creating...' : 'Create User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit User Modal */}
          {showEditModal && editingUser && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-medium mb-4">Edit User</h3>
                <form onSubmit={handleEditUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      name="email"
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password (optional)</label>
                    <input
                      name="password"
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Leave blank to keep current"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      name="role"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.role}
                      onChange={handleInputChange}
                    >
                      <option value="USER">User</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </div>
                  {formMessage && (
                    <div className={`p-3 rounded-md text-sm ${
                      formMessage.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      {formMessage}
                    </div>
                  )}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                    >
                      {formLoading ? 'Updating...' : 'Update User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingUser(null);
                        setFormData({ email: '', password: '', role: 'USER' });
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rules Management - All Users */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Monitoring Rules</h3>
          <button
            onClick={() => setShowAddRuleModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
          >
            + Add Rule
          </button>
        </div>

        {/* Rules Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {rulesLoading ? (
            <div className="p-8 text-center text-gray-500">Loading rules...</div>
          ) : rulesError ? (
            <div className="p-8 text-center text-red-600">{rulesError}</div>
          ) : rules.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No rules found. Create your first rule!</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keywords</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platforms</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categories</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-xs truncate">{rule.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                          <div className="flex flex-wrap gap-1">
                            {rule.keywords.slice(0, 3).map((kw, i) => (
                              <span key={i} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">{kw}</span>
                            ))}
                            {rule.keywords.length > 3 && <span className="text-xs text-gray-500">+{rule.keywords.length - 3}</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">{rule.platforms.length}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">{rule.categories.length}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            rule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {rule.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(rule.createdAt).toLocaleDateString()} by {rule.createdBy}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEditRuleClick(rule)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Add Rule Modal */}
        {showAddRuleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-medium mb-4">Add Monitoring Rule</h3>
              <form onSubmit={handleAddRule} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    name="name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.name}
                    onChange={handleRuleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Keywords (comma separated)</label>
                  <textarea
                    name="keywords"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.keywords}
                    onChange={handleRuleInputChange}
                    placeholder="brand1, keyword2, DFSK, media"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter keywords separated by commas</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Platforms (comma separated)</label>
                    <input
                      name="platforms"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={ruleFormData.platforms}
                      onChange={handleRuleInputChange}
                      placeholder="kompas.com, detik.com, tempo.co"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Categories (comma separated)</label>
                    <input
                      name="categories"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={ruleFormData.categories}
                      onChange={handleRuleInputChange}
                      placeholder="otomotif, berita, ekonomi"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.description}
                    onChange={handleRuleInputChange}
                    placeholder="Optional description of this rule"
                  />
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={ruleFormData.isActive}
                    onChange={(e) => setRuleFormData({...ruleFormData, isActive: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
                {ruleFormMessage && (
                  <div className={`p-3 rounded-md text-sm ${
                    ruleFormMessage.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    {ruleFormMessage}
                  </div>
                )}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={ruleFormLoading}
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                  >
                    {ruleFormLoading ? 'Creating...' : 'Create Rule'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddRuleModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Rule Modal - similar to Add */}
        {showEditRuleModal && editingRule && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-medium mb-4">Edit Rule: {editingRule.name}</h3>
              <form onSubmit={handleEditRule} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    name="name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.name}
                    onChange={handleRuleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Keywords (comma separated)</label>
                  <textarea
                    name="keywords"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.keywords}
                    onChange={handleRuleInputChange}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Platforms (comma separated)</label>
                    <input
                      name="platforms"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.platforms}
                    onChange={handleRuleInputChange}
                  />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Categories (comma separated)</label>
                    <input
                      name="categories"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={ruleFormData.categories}
                      onChange={handleRuleInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={ruleFormData.description}
                    onChange={handleRuleInputChange}
                  />
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={ruleFormData.isActive}
                    onChange={(e) => setRuleFormData({...ruleFormData, isActive: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
                {ruleFormMessage && (
                  <div className={`p-3 rounded-md text-sm ${
                    ruleFormMessage.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    {ruleFormMessage}
                  </div>
                )}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={ruleFormLoading}
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                  >
                    {ruleFormLoading ? 'Updating...' : 'Update Rule'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditRuleModal(false);
                      setEditingRule(null);
                      setRuleFormData({ name: '', keywords: '', platforms: '', categories: '', description: '', isActive: true });
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
